import React, { useEffect } from "react";
import { Button } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
  Inject,
  ListViewComponent,
  Virtualization,
} from "@syncfusion/ej2-react-lists";
import { FC } from "react";
import { closeGridMetricsPickerDialog } from "./GridMetricsPickerDialog";
import { GridMetricOptionValueReadDTO } from "../types";
import { EnumSingleSelectFieldV2 } from "../../../../../Fields";
import { EnumSingleSelectFieldValue } from "../../../../../Fields/types/fieldTypes";
import { PrimaryButton, SecondaryButton } from "../../../../../Buttons";
import { Footer } from "../../../../../Dialogs";
import AppToasterV2 from "../../../../../ToastV2/ToastV2";

export interface GridMetricsPickerFormProps {
  viewId: string;
  updateViewMetricSettings: (
    viewId: string,
    metricSettings: string[]
  ) => Promise<void>;
  fetchMetricOptionValues: () => Promise<void | GridMetricOptionValueReadDTO[]>;
  metricConfiguration: string[];
}

type ListMetric = { id: string; text: string };

export const GridMetricsPickerForm: FC<GridMetricsPickerFormProps> = (
  props
) => {
  const [originalMetricConfiguration, setOriginalMetricConfiguration] =
    React.useState<string[]>(props.metricConfiguration);

  const [localMetricConfiguration, setLocalMetricConfiguration] =
    React.useState<string[]>(props.metricConfiguration);

  const [metricOptions, setMetricOptions] = React.useState<
    GridMetricOptionValueReadDTO[]
  >([]);

  const [disableForm, setDisableForm] = React.useState(false);

  const getMetricOptions = async () => {
    const metricOptions = await props.fetchMetricOptionValues();

    if (metricOptions) {
      setMetricOptions(metricOptions);
    }
  };

  useEffect(() => {
    getMetricOptions();
  }, []);

  const addMetric = (metricId: string) => {
    if(metricId === undefined){
      return;
    }
    // Validate
    if (
      props.metricConfiguration &&
      localMetricConfiguration.map((x) => x).includes(metricId)
    ) {
      throw `Cannot add ${metricId} since its already on the list.`;
    }

    // Get configuration
    const col = metricOptions.find((x) => x.id === metricId);
    if (!col) {
      throw `Cannot add ${metricId} sinc eits already on the list.`;
    }

    // Add
    localMetricConfiguration.push(col.id);
    setLocalMetricConfiguration([...localMetricConfiguration]);
  };
  const removeMetric = (field: string) => {
    setLocalMetricConfiguration(
      localMetricConfiguration.filter((x) => x !== field)
    );
  };

  const columnTemplate = (data: ListMetric): React.ReactNode => {
    return (
      <div className="view-metric">
        <span>{data.text}</span>
        <Button
          title="Remove"
          className="view-metric-remove"
          minimal={true}
          icon={IconNames.BAN_CIRCLE}
          disabled={disableForm}
          onClick={() => {
            removeMetric(data.id);
          }}
        />
      </div>
    );
  };

  const getListColumns = (): ListMetric[] => {
    return metricOptions
      .filter((x) => localMetricConfiguration.includes(x.id))
      .map((x) => {
        return { text: x.title, id: x.id };
      });
  };

  const cancelChanges = () => {
    setLocalMetricConfiguration([...originalMetricConfiguration]);

    // Delegate
    closeGridMetricsPickerDialog();
  };

  const saveView = async () => {
    // Disable
    setDisableForm(true);

    // Save
    await props
      .updateViewMetricSettings(
        props.viewId,
        localMetricConfiguration.map((x) => x)
      )
      .then(() => {
        // Default
        setOriginalMetricConfiguration([...localMetricConfiguration]);

        AppToasterV2.showSuccessAppToaster("Metrics Configuration Saved");

        // Delegate
        closeGridMetricsPickerDialog();
      })
      .catch(() => {
        setDisableForm(false);

        AppToasterV2.showFailedActionToaster(
          "An error occurred executing the current action."
        );
      });
  };

  return (
    <section className="ucl-grid-metrics-picker">
      <div className="ucl-grid-metrics-picker-search">
        <label>Search for the fields you would like to add to this view.</label>
        {metricOptions && (
          <EnumSingleSelectFieldV2
            placeholder="Type to search..."
            optionValues={metricOptions
              .filter((x) => !localMetricConfiguration.includes(x.id))
              .map((option) => ({
                label: option.title,
                value: option.id,
              }))}
            onSubmit={(value: EnumSingleSelectFieldValue) => {
              addMetric(value as string);
            }}
            type={"EnumSingleSelect"}
          />
        )}
      </div>
      <div className="ucl-grid-metrics-picker-metrics">
        <label>Added Fields</label>
        <ListViewComponent
          id="ucl-grid-metrics-picker-metrics-list"
          enableVirtualization={true}
          template={columnTemplate}
          dataSource={getListColumns()}
          enable={!disableForm}
        >
          <Inject services={[Virtualization]} />
        </ListViewComponent>
      </div>
      <Footer className="ucl-grid-metrics-picker-footer">
        <SecondaryButton
          className="ucl-grid-metrics-picker-cancel"
          text="Cancel"
          disabled={disableForm}
          onClick={cancelChanges}
        />
        <PrimaryButton
          className="ucl-grid-metrics-picker-save"
          disabled={disableForm}
          onClick={saveView}
          text="Save"
          isLoading={disableForm}
        />
      </Footer>
    </section>
  );
};
